import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { Menu, X, Globe, Phone, Mail, MapPin, ChevronDown, Facebook, Instagram, Youtube } from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_experiencias-travel/artifacts/eufjmn3t_logo%20png.png';
const FOOTER_LOGO_URL = 'https://customer-assets.emergentagent.com/job_experiencias-travel/artifacts/wlltvdqd_7X7_blue%20bg-01.png';

export const Header = () => {
  const { t, language, setLanguage } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const navItems = [
    { path: '/', label: t('nav.home') },
    { path: '/about', label: t('nav.about') },
    { path: '/services', label: t('nav.services') },
    { path: '/products', label: t('nav.products') },
    { path: '/team', label: t('nav.team') },
    { path: '/technology', label: t('nav.technology') },
    { path: '/group-request', label: t('nav.groupRequest') },
    { path: '/contact', label: t('nav.contact') },
  ];

  const languages = [
    { code: 'pt', label: 'Português', flag: '🇵🇹' },
    { code: 'en', label: 'English', flag: '🇬🇧' },
    { code: 'es', label: 'Español', flag: '🇪🇸' },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/95 backdrop-blur-md shadow-lg'
          : 'bg-[#0F172A]/80 backdrop-blur-sm'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex-shrink-0">
            <motion.img
              src={LOGO_URL}
              alt="7x7 Travel"
              className="h-12 w-auto"
              whileHover={{ scale: 1.05 }}
              transition={{ duration: 0.2 }}
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors duration-200 ${
                  location.pathname === item.path
                    ? isScrolled 
                      ? 'text-[#1E3A8A] bg-[#1E3A8A]/10'
                      : 'text-[#00D4FF] bg-white/10'
                    : isScrolled
                    ? 'text-slate-700 hover:text-[#1E3A8A] hover:bg-slate-100'
                    : 'text-white hover:text-[#00D4FF] hover:bg-white/10'
                }`}
                data-testid={`nav-${item.path.replace('/', '') || 'home'}`}
              >
                {item.label}
              </Link>
            ))}
          </nav>

          {/* Right Side */}
          <div className="flex items-center gap-3">
            {/* Language Selector */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className={`flex items-center gap-2 ${isScrolled ? 'text-slate-700' : 'text-white'}`}
                  data-testid="language-selector"
                >
                  <Globe className="h-4 w-4" />
                  <span className="hidden sm:inline">
                    {languages.find((l) => l.code === language)?.flag}
                  </span>
                  <ChevronDown className="h-3 w-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {languages.map((lang) => (
                  <DropdownMenuItem
                    key={lang.code}
                    onClick={() => setLanguage(lang.code)}
                    className={language === lang.code ? 'bg-slate-100' : ''}
                    data-testid={`lang-${lang.code}`}
                  >
                    <span className="mr-2">{lang.flag}</span>
                    {lang.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Agencies Button */}
            <a
              href="https://www.7x7travel.pt"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="agencies-btn"
            >
              <Button
                className="hidden sm:flex bg-[#1E3A8A] hover:bg-[#1E3A8A]/90 text-white rounded-full px-6"
              >
                {t('nav.agencies')}
              </Button>
            </a>

            {/* Mobile Menu Button */}
            <Button
              variant="ghost"
              size="icon"
              className={`lg:hidden ${isScrolled ? 'text-slate-700' : 'text-white'}`}
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </Button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-white border-t border-slate-200"
          >
            <div className="px-4 py-4 space-y-2">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`block px-4 py-3 rounded-lg text-base font-medium transition-colors ${
                    location.pathname === item.path
                      ? 'text-[#1E3A8A] bg-[#1E3A8A]/10'
                      : 'text-slate-700 hover:bg-slate-100'
                  }`}
                >
                  {item.label}
                </Link>
              ))}
              <a
                href="https://www.7x7travel.pt"
                target="_blank"
                rel="noopener noreferrer"
                className="block"
              >
                <Button className="w-full mt-4 bg-[#1E3A8A] hover:bg-[#1E3A8A]/90 text-white rounded-full">
                  {t('nav.agencies')}
                </Button>
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export const Footer = () => {
  const { t } = useLanguage();

  const quickLinks = [
    { path: '/', label: t('nav.home') },
    { path: '/about', label: t('nav.about') },
    { path: '/services', label: t('nav.services') },
    { path: '/products', label: t('nav.products') },
    { path: '/team', label: t('nav.team') },
    { path: '/contact', label: t('nav.contact') },
  ];

  return (
    <footer className="bg-[#0F172A] text-white">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Logo & Description */}
          <div className="lg:col-span-1">
            <img
              src={FOOTER_LOGO_URL}
              alt="7x7 Travel"
              className="h-12 w-auto mb-6"
            />
            <p className="text-slate-400 text-sm leading-relaxed">
              {t('footer.description')}
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-semibold mb-6 text-[#00D4FF]">
              {t('footer.quickLinks')}
            </h3>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-slate-400 hover:text-white transition-colors text-sm"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div className="lg:col-span-2">
            <h3 className="text-lg font-semibold mb-6 text-[#00D4FF]">
              {t('footer.contactInfo')}
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div className="flex items-start gap-3">
                <MapPin className="h-5 w-5 text-[#00D4FF] flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-slate-300 text-sm">{t('footer.address')}</p>
                  <p className="text-slate-300 text-sm">{t('footer.city')}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Phone className="h-5 w-5 text-[#00D4FF] flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-slate-300 text-sm">{t('footer.phone')}: 221 450 880</p>
                  <p className="text-slate-300 text-sm">{t('footer.emergency')}: 92 850 1136</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Mail className="h-5 w-5 text-[#00D4FF] flex-shrink-0 mt-0.5" />
                <p className="text-slate-300 text-sm">info@7x7travel.pt</p>
              </div>
              <div className="flex items-start gap-3">
                <div className="flex gap-4">
                  <a
                    href="https://www.facebook.com/7x7travel"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-full bg-[#00D4FF]/10 flex items-center justify-center hover:bg-[#00D4FF]/30 transition-colors"
                    data-testid="social-facebook"
                  >
                    <Facebook className="h-5 w-5 text-[#00D4FF]" />
                  </a>
                  <a
                    href="https://www.instagram.com/7x7travel/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-full bg-[#00D4FF]/10 flex items-center justify-center hover:bg-[#00D4FF]/30 transition-colors"
                    data-testid="social-instagram"
                  >
                    <Instagram className="h-5 w-5 text-[#00D4FF]" />
                  </a>
                  <a
                    href="https://www.youtube.com/@7x7Travel"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-full bg-[#00D4FF]/10 flex items-center justify-center hover:bg-[#00D4FF]/30 transition-colors"
                    data-testid="social-youtube"
                  >
                    <Youtube className="h-5 w-5 text-[#00D4FF]" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Certifications & Partners */}
        <div className="mt-12 pt-8 border-t border-slate-800">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2">
              <span className="text-[#00D4FF] font-semibold text-sm">RNAVT 8180</span>
              <span className="text-slate-500">|</span>
              <span className="text-[#00D4FF] font-semibold text-sm">TP201302</span>
            </div>
            <div className="flex items-center gap-6">
              <a href="https://www.turismodeportugal.pt" target="_blank" rel="noopener noreferrer" className="block">
                <img 
                  src="https://customer-assets.emergentagent.com/job_experiencias-travel/artifacts/5wohquvn_Turismo_de_Portugal_Logo-700x241-1.png" 
                  alt="Turismo de Portugal" 
                  className="h-10 w-auto brightness-0 invert opacity-70 hover:opacity-100 transition-opacity"
                />
              </a>
              <a href="https://www.grupogea.es" target="_blank" rel="noopener noreferrer" className="block">
                <img 
                  src="https://customer-assets.emergentagent.com/job_experiencias-travel/artifacts/6e6385rv_grupo-gea.jpg" 
                  alt="Grupo GEA" 
                  className="h-10 w-auto rounded bg-white p-1"
                />
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-slate-500 text-sm">
              © {new Date().getFullYear()} 7x7 Travel. {t('footer.rights')}
            </p>
            <div className="flex items-center gap-6">
              <a href="https://www.7x7travel.pt" target="_blank" rel="noopener noreferrer" className="text-slate-500 hover:text-[#00D4FF] transition-colors text-sm">
                www.7x7travel.pt
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export const Layout = ({ children }) => {
  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Header />
      <main>{children}</main>
      <Footer />
    </div>
  );
};
